// Branding suggested from the company website (getWebsiteBranding): what
// the admin can pick and how a pick becomes form changes or an upload.

// What the fetch can find, in the order the dialog lists them.
export const WEBSITE_BRANDING_ITEMS = [
  { key: "logo", label: "Logo" },
  { key: "bannerImage", label: "Banner image" },
  { key: "primaryColor", label: "Primary colour" },
  { key: "secondaryColor", label: "Secondary colour" },
  { key: "fontFamily", label: "Font" },
  { key: "favicon", label: "Favicon" },
];

const EXTENSIONS = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/webp": ".webp",
  "image/x-icon": ".ico",
};

// Whether the website gave something for this item. The font counts when it
// is one of ours or the site's own font can be used (customFont).
export const foundOnWebsite = (result, key) =>
  key === "fontFamily"
    ? !!(result?.fontFamily || result?.customFont)
    : !!result?.[key];

// For an <img> preview.
export const websiteImageUrl = (image) =>
  image ? `data:${image.contentType};base64,${image.data}` : null;

// A File the normal upload routes accept (the extension comes from the
// image's real type, which the server checked).
export const fileFromWebsiteImage = (image, baseName) => {
  const binary = atob(image.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File(
    [bytes],
    `${baseName}${EXTENSIONS[image.contentType] || ""}`,
    {
      type: image.contentType,
    }
  );
};

// A copied font file, named after the font and its weight.
export const fileFromWebsiteFont = (fontFile, fontName) => {
  const binary = atob(fontFile.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const base =
    `${fontName}-${fontFile.weight}${fontFile.style === "italic" ? "-italic" : ""}`
      .replace(/[^A-Za-z0-9-]+/g, "-")
      .toLowerCase();
  return new File([bytes], `${base}.${fontFile.format}`, {
    type: `font/${fontFile.format}`,
  });
};

// Everything found starts ticked, except:
// - the logo when there is one already: it is saved at once and also
//   replaces the logo in Company Settings;
// - the site's own font files: copying them is the admin's call (licence).
export const initialWebsiteChoices = (result, { hasLogo = false } = {}) =>
  Object.fromEntries(
    WEBSITE_BRANDING_ITEMS.map(({ key }) => {
      if (!foundOnWebsite(result, key)) return [key, false];
      if (key === "logo") return [key, !hasLogo];
      if (key === "fontFamily")
        return [
          key,
          !!result.fontFamily || result.customFont?.source === "google",
        ];
      return [key, true];
    })
  );

// Form changes for the picked colours and font (images are uploaded first
// and add their own path).
export const websiteFormChanges = (result, chosen) => {
  const changes = {};
  if (chosen.primaryColor && result.primaryColor)
    changes.brandColor = result.primaryColor;
  if (chosen.secondaryColor && result.secondaryColor)
    changes.secondaryColor = result.secondaryColor;
  if (chosen.fontFamily && result.fontFamily)
    changes.fontFamily = String(result.fontFamily).toLowerCase();
  // A Google Font is used by name; copied files are added after upload.
  else if (chosen.fontFamily && result.customFont?.source === "google")
    Object.assign(changes, {
      fontFamily: "custom",
      customFontName: safeFontName(result.customFont.name),
      customFontSource: "google",
      customFontFiles: [],
    });
  return changes;
};

// A font name the page can use (letters, numbers, spaces, dots, dashes,
// underscores; up to 60): "Söhne-Var" -> "S hne-Var".
export const safeFontName = (name) =>
  String(name || "")
    .replace(/[^A-Za-z0-9 _.-]+/g, " ")
    .replace(/^[^A-Za-z0-9]+/, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60) || "Custom font";

// "https://www.acme.com/en/" -> "acme.com".
export const siteName = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};
